import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  TextareaAutosize,
  TextField,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import StopIcon from '@mui/icons-material/Stop'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import KeyboardVoiceIcon from '@mui/icons-material/KeyboardVoice'
import { HOME_OPTIONS, VAC_OPTIONS } from '../../util/visitOptions'
import useVoskDictation from '../../util/useVoskDictation'

export default function VisitEditor({
  classes,
  visit,
  onChange,
  isAdmin,
  voiceError,
  voiceNotes = [],
  voiceLoading,
  onUploadVoice,
  onDeleteVoice,
}) {
  const [isRecording, setIsRecording] = useState(false)
  const [recordingError, setRecordingError] = useState('')
  const [voiceTitle, setVoiceTitle] = useState('')
  const [recordedAudio, setRecordedAudio] = useState(null)
  const recorderRef = useRef(null)
  const streamRef = useRef(null)
  const chunksRef = useRef([])

  const {
    isTranscribing,
    activeDictationField,
    transcriptionError,
    startDictation,
    stopDictation,
  } = useVoskDictation()

  const pushVisitField = (fieldName, value) => {
    onChange({
      target: {
        name: fieldName,
        value,
      },
    })
  }

  const applySmartDictationFormatting = (text) => {
    let formatted = String(text || '')
    formatted = formatted.replace(/\bi\b/g, 'I')
    formatted = formatted.replace(/(^\s*[a-z])|([.!?]\s+[a-z])/g, (segment) => segment.toUpperCase())
    return formatted
  }

  const getSupportedMimeType = () => {
    if (typeof MediaRecorder === 'undefined') return ''

    const preferredTypes = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus',
      'audio/ogg',
    ]

    const supportedType = preferredTypes.find((mimeType) => MediaRecorder.isTypeSupported(mimeType))
    return supportedType || ''
  }

  const previewUrl = useMemo(() => {
    if (!recordedAudio) return ''
    return URL.createObjectURL(recordedAudio)
  }, [recordedAudio])

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }
    }
  }, [previewUrl])

  const handleStartRecording = async () => {
    setRecordingError('')
    setRecordedAudio(null)

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || typeof MediaRecorder === 'undefined') {
        setRecordingError('Audio recording is not supported in this browser.')
        return
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const mimeType = getSupportedMimeType()
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
      chunksRef.current = []

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      recorder.onstop = () => {
        const mimeType = recorder.mimeType || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type: mimeType })
        if (!blob || blob.size === 0) {
          setRecordedAudio(null)
          setRecordingError('The recording is empty. Please record again and speak for a few seconds before stopping.')
        } else {
          console.log('Voice note recorded', { size: blob.size, type: blob.type, chunks: chunksRef.current.length })
          setRecordedAudio(blob)
          setRecordingError('')
        }
        setIsRecording(false)
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop())
        }
      }

      recorderRef.current = recorder
      recorder.start(250)
      setIsRecording(true)
    } catch (error) {
      setRecordingError('Unable to start audio recording. Check browser microphone permission.')
      console.error('Unable to start audio recording', error)
    }
  }

  const handleStopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try {
        recorderRef.current.requestData()
      } catch (error) {
        console.error('Unable to flush recorder data', error)
      }
      recorderRef.current.stop()
    }
  }

  const handleUploadRecordedAudio = async () => {
    if (!recordedAudio || recordedAudio.size === 0) {
      setRecordingError('The current recording is empty and cannot be uploaded.')
      return
    }
    const uploadSucceeded = await onUploadVoice(recordedAudio, voiceTitle)
    if (uploadSucceeded) {
      setRecordedAudio(null)
      setVoiceTitle('')
    }
  }

  const handleStartDictation = (fieldName) => {
    if (isTranscribing && activeDictationField === fieldName) return
    const currentText = String(visit[fieldName] || '')
    startDictation(fieldName, currentText, (text) => {
      pushVisitField(fieldName, applySmartDictationFormatting(text))
    })
  }

  const handleStopDictation = () => {
    stopDictation()
  }

  const renderDictationControls = (fieldName, label) => {
    const isActive = isTranscribing && activeDictationField === fieldName

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 148, maxWidth: 152 }}>
        <Typography variant="caption" color="textSecondary">
          Voice to text
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<KeyboardVoiceIcon />}
          onClick={() => handleStartDictation(fieldName)}
          disabled={isActive}
        >
          {isActive ? `Listening (${label})` : `Dictate ${label}`}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<StopIcon />}
          onClick={handleStopDictation}
          disabled={!isActive}
        >
          Stop
        </Button>
      </div>
    )
  }

  return (
    <div>
      <form className={classes.form} noValidate>
        <Grid container item xs={12} spacing={1}>
          <Grid item xs={4}>
            <TextField
              variant="standard"
              id="home"
              label="Location"
              name="home"
              value={visit['home']}
              onChange={onChange}
              select
            >
              {HOME_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={4}>
            <TextField
              variant="standard"
              id="vac"
              label="Category"
              name="vac"
              value={visit['vac']}
              onChange={onChange}
              select
            >
              {VAC_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={2}>
            <TextField
              variant="standard"
              id="admin"
              label="Consumer Fee"
              name="admin"
              value={visit['admin']}
              onChange={onChange}
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              variant="standard"
              id="seenBy"
              label="Sr Seen By"
              name="seenBy"
              value={visit['seenBy']}
              onChange={onChange}
            />
          </Grid>
        </Grid>
        <div className={classes.formSeparator1}>
          <Typography variant="h6" className={classes.title}>
            Notes, Reminders, Queries
          </Typography>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <TextareaAutosize
              style={{ width: '82%', minWidth: 280, fontSize: '14pt', flex: '1 1 620px' }}
              minRows={6}
              maxRows={6}
              placeholder="type here..."
              name="notes"
              value={visit['notes']}
              onChange={onChange}
            />
            {renderDictationControls('notes', 'Notes')}
          </div>
        </div>
        <div className={classes.formSeparator1}>
          <Typography variant="h6" className={classes.title}>
            Admin Notes
          </Typography>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <TextareaAutosize
              style={{ width: '82%', minWidth: 280, fontSize: '14pt', flex: '1 1 620px' }}
              minRows={6}
              maxRows={6}
              placeholder="type here..."
              name="admnotes"
              value={visit['admnotes']}
              onChange={onChange}
            />
            {renderDictationControls('admnotes', 'Admin')}
          </div>
          {transcriptionError && (
            <Typography variant="body2" color="error" style={{ marginTop: 6 }}>
              {transcriptionError}
            </Typography>
          )}
        </div>

        <div className={classes.formSeparator1}>
          <Typography variant="h6" className={classes.title}>
            Voice Notes
          </Typography>
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<FiberManualRecordIcon />}
                onClick={handleStartRecording}
                disabled={isRecording}
              >
                Record
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                size="small"
                startIcon={<StopIcon />}
                onClick={handleStopRecording}
                disabled={!isRecording}
              >
                Stop
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                label="Voice Title"
                value={voiceTitle}
                onChange={(event) => setVoiceTitle(event.target.value)}
              />
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<CloudUploadIcon />}
                onClick={handleUploadRecordedAudio}
                disabled={!recordedAudio || voiceLoading}
              >
                Upload Voice
              </Button>
            </Grid>
          </Grid>

          {isRecording && (
            <Typography variant="body2" style={{ marginTop: 6 }}>
              Recording in progress...
            </Typography>
          )}

          {recordingError && (
            <Typography variant="body2" color="error" style={{ marginTop: 6 }}>
              {recordingError}
            </Typography>
          )}

          {voiceError && (
            <Typography variant="body2" color="error" style={{ marginTop: 6 }}>
              {voiceError}
            </Typography>
          )}

          {recordedAudio && previewUrl && (
            <div style={{ marginTop: 10 }}>
              <Typography variant="body2">Recorded preview:</Typography>
              <Typography variant="caption" display="block" style={{ marginTop: 4 }}>
                {`Type: ${recordedAudio.type || 'unknown'} | Size: ${recordedAudio.size} bytes`}
              </Typography>
              <audio controls src={previewUrl} style={{ width: '100%', marginTop: 4 }} />
            </div>
          )}

          {voiceLoading && (
            <CircularProgress size={24} style={{ marginTop: 8 }} />
          )}

          {voiceNotes.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {voiceNotes.map((note) => (
                <div key={note.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <Typography variant="body2">{note.title || 'Voice Note'}</Typography>
                    <audio controls src={note.audioUrl} style={{ width: '100%' }} />
                  </div>
                  {isAdmin ? (
                    <IconButton onClick={() => onDeleteVoice(note.id)}>
                      <DeleteIcon />
                    </IconButton>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </form>
    </div>
  )
}
